import axios from 'axios';
import { FullStoryAPI } from 'react-fullstory';
import { NotificationManager } from 'react-notifications';

import {
  getAuthenticatedToken,
  removePersist,
  setAuthenticatedToken,
  setRefreshToken,
} from '../../utils/cacheStore';

import { t } from '../../i18n';
import { cloneDeep, get } from '../../utils/lodash';
import { getCommonError } from '../../utils';
import { authMsg, userAuthMgr } from '../../utils/auth';

const CLEAR_AUTH = 'CLEAR_AUTH';
const LOGIN_USER_LOADING = 'LOGIN_USER_LOADING';
const LOGIN_AS_USER_LOADING = 'LOGIN_AS_USER_LOADING';
const GET_USER_LOADING = 'GET_USER_LOADING';
const GET_USER = 'GET_USER';
const USER_LOGIN_INCORRECT = 'USER_LOGIN_INCORRECT';
const UPDATE_PASSWORD = 'UPDATE_PASSWORD';
const FORGOT_USER_LOADING = 'FORGOT_USER_LOADING';
const RESET_USER_LOADING = 'RESET_USER_LOADING';
const REGISTER_LOADING = 'REGISTER_LOADING';
const REGISTER_USER_LOADING = 'REGISTER_USER_LOADING';
const SET_REG_REQUEST_USER = 'SET_REG_REQUEST_USER';
const USER_MAIL_VERIFICATION_LOADING = 'USER_MAIL_VERIFICATION_LOADING';
const USER_MAIL_VERIFICATION = 'USER_MAIL_VERIFICATION';
const SET_AUTH_TOKEN = 'SET_AUTH_TOKEN';
const UPDATE_USER_LOADING = 'UPDATE_USER_LOADING';
const SET_ORGANIZATION = 'SET_ORGANIZATION';
const SET_SIGNUP_DATA = 'SET_SIGNUP_DATA';
const ERROR_HANDLING = 'ERROR_HANDLING';
const SET_IMPERSONATED_BY = 'SET_IMPERSONATED_BY';
const SET_IS_SUPER_ADMIN = 'SET_IS_SUPER_ADMIN';

const initialState = {
  user: {},
  userGetLoading: false,
  userLoginLoading: false,
  userLoginAsLoading: false,
  userLogoutLoading: false,
  userLoginIncorrect: false,
  updatePasswordLoading: false,
  forgotPassLoading: false,
  resetPassLoading: false,
  registerTokenLoading: false,
  registerLoading: false,
  requestedRegUser: {},
  emailVerify: null,
  emailVerifyLoading: false,
  authToken: null,
  updatingUser: false,
  userSignupData: {},
  errorHandling: null,
  impersonatedBy: null,
  isSuperAdmin: false,
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case CLEAR_AUTH:
      return initialState;
    case LOGIN_USER_LOADING:
      return { ...state, userLoginLoading: action.payload };
    case LOGIN_AS_USER_LOADING:
      return { ...state, userLoginAsLoading: action.payload };
    case USER_LOGIN_INCORRECT:
      return { ...state, userLoginIncorrect: action.payload };
    case GET_USER:
      return { ...state, user: action.payload || {} };
    case GET_USER_LOADING:
      return { ...state, userGetLoading: action.payload };
    case UPDATE_PASSWORD:
      return { ...state, updatePasswordLoading: action.payload };
    case FORGOT_USER_LOADING:
      return { ...state, forgotPassLoading: action.payload };
    case RESET_USER_LOADING:
      return { ...state, resetPassLoading: action.payload };
    case REGISTER_USER_LOADING:
      return { ...state, registerLoading: action.payload };
    case REGISTER_LOADING:
      return { ...state, registerTokenLoading: action.payload };
    case SET_REG_REQUEST_USER:
      return { ...state, requestedRegUser: action.payload };
    case USER_MAIL_VERIFICATION_LOADING:
      return { ...state, emailVerifyLoading: action.payload };
    case USER_MAIL_VERIFICATION:
      return { ...state, emailVerify: action.payload };
    case SET_AUTH_TOKEN:
      return { ...state, authToken: action.payload };
    case UPDATE_USER_LOADING:
      return { ...state, updatingUser: action.payload };
    case SET_ORGANIZATION:
      return { ...state, archiveCoreData: action.payload };
    case SET_SIGNUP_DATA:
      return { ...state, userSignupData: action.payload };
    case ERROR_HANDLING:
      return { ...state, errorHandling: action.payload };
    case SET_IMPERSONATED_BY:
      return { ...state, impersonatedBy: action.payload };
    case SET_IS_SUPER_ADMIN:
      return { ...state, isSuperAdmin: action.payload };
    // case CLEAR_SIGNUP_DATA:
    //   return { ...state, userSignupData: {} };
    default:
      return state;
  }
}

/** Decode a JWT's payload without verifying the signature (client-side only). */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function signUpCache(values) {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_SIGNUP_DATA, payload: values });
    } catch (error) {
      authMsg('error', getCommonError(error));
    }
  };
}

export function clearSignUpCache() {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_SIGNUP_DATA, payload: {} });
    } catch (error) {
      authMsg('error', getCommonError(error));
    }
  };
}

export function initializedApp() {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const authToken = await getAuthenticatedToken();
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        resolve();
      } catch (error) {
        reject();
      }
    });
  };
}

export function clearAuth() {
  return async (dispatch) => {
    dispatch({ type: CLEAR_AUTH });
  };
}

export function loginUser(form) {
  return async (dispatch) => {
    dispatch({ type: LOGIN_USER_LOADING, payload: true });
    return new Promise(async (resolve, reject) => {
      try {
        let { data } = await axios.post('/auth/login', form, {
          withCredentials: true,
        });
        await dispatch({ type: USER_LOGIN_INCORRECT, payload: false });
        await dispatch({ type: LOGIN_USER_LOADING, payload: false });
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        await setAuthenticatedToken(data.token);
        await setRefreshToken(data.refreshToken);
        const payload = decodeJwtPayload(data.token);
        dispatch({
          type: SET_IS_SUPER_ADMIN,
          payload: payload?.isSuperAdmin ?? false,
        });
        await dispatch(initializedApp());
        resolve();
      } catch (error) {
        dispatch({ type: LOGIN_USER_LOADING, payload: false });
        dispatch({ type: GET_USER, payload: {} });
        // dispatch({ type: ERROR_HANDLING, payload: getCommonError(error) });
        // NotificationManager.error(getCommonError(error));
        reject(getCommonError(error));
      }
    });
  };
}

export function loginAsUser(id) {
  return async (dispatch) => {
    dispatch({ type: LOGIN_AS_USER_LOADING, payload: true });
    return new Promise(async (resolve, reject) => {
      try {
        let { data } = await axios.post(
          '/auth/login-as',
          { id },
          {
            withCredentials: true,
          }
        );
        await dispatch({ type: USER_LOGIN_INCORRECT, payload: false });
        await dispatch({ type: LOGIN_AS_USER_LOADING, payload: false });
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        await setAuthenticatedToken(data.token);
        await setRefreshToken(data.refreshToken);

        // Decode the impersonation JWT to extract the original admin ID
        const payload = decodeJwtPayload(data.token);
        dispatch({
          type: SET_IMPERSONATED_BY,
          payload: payload?.impersonatedBy ?? null,
        });
        dispatch({
          type: SET_IS_SUPER_ADMIN,
          payload: payload?.isSuperAdmin ?? false,
        });

        await dispatch(initializedApp());
        resolve();
      } catch (error) {
        dispatch({ type: LOGIN_AS_USER_LOADING, payload: false });
        dispatch({ type: GET_USER, payload: {} });
        // dispatch({ type: ERROR_HANDLING, payload: getCommonError(error) });
        // NotificationManager.error(getCommonError(error));
        reject(getCommonError(error));
      }
    });
  };
}

export function revertImpersonation(navigate = null) {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(
        '/auth/revert-impersonation',
        {},
        { withCredentials: true }
      );
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      await setAuthenticatedToken(data.token);
      await setRefreshToken(data.refreshToken);
      const payload = decodeJwtPayload(data.token);
      dispatch({ type: SET_IMPERSONATED_BY, payload: null });
      dispatch({
        type: SET_IS_SUPER_ADMIN,
        payload: payload?.isSuperAdmin ?? false,
      });
      await dispatch(initializedApp());
      navigate && navigate('/admin/activity-settings');
    } catch (error) {
      // Clear impersonation state first so logoutUser doesn't recurse back into revertImpersonation
      dispatch({ type: SET_IMPERSONATED_BY, payload: null });
      dispatch(logoutUser(navigate));
    }
  };
}

export function logoutUser(navigate = null) {
  return async (dispatch, getState) => {
    try {
      await axios.get('/auth/logout', { withCredentials: true });
      await userAuthMgr.signout();

      navigate && navigate('/');

      await dispatch({ type: 'RESET' });
      removePersist();
    } catch (error) {
      await userAuthMgr.signout();
      navigate && navigate('/');
      await dispatch({ type: 'RESET' });
      removePersist();
    }
  };
}

export function getUserProfile() {
  return async (dispatch) => {
    dispatch({ type: GET_USER_LOADING, payload: true });
    try {
      const res = await axios.get(`/user/current`);
      dispatch({ type: GET_USER, payload: res.data });
      FullStoryAPI('identify', res.data.id, {
        displayName: res.data.name,
        email: res.data.email,
        organisationId: get(res.data, 'organisationId', null),
        organisationName: get(res.data, 'organisation.name', null),
        role: res.data.role,
      });
      dispatch({ type: GET_USER_LOADING, payload: false });
    } catch (error) {
      dispatch({ type: GET_USER_LOADING, payload: false });
      dispatch(logoutUser());
    }
  };
}

export function getUserProfileOnly() {
  return async (dispatch) => {
    try {
      const res = await axios.get(`/user/current`);
      dispatch({ type: GET_USER, payload: res.data });
    } catch (error) {
      authMsg('error', getCommonError(error));
    }
  };
}

export function updateUser(body) {
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_USER_LOADING, payload: true });
      await axios.put('/user', body);
      authMsg('success', [
        t('store.messages.profile_updated'),
        t('common.success'),
      ]);
      dispatch({ type: UPDATE_USER_LOADING, payload: false });
    } catch (error) {
      dispatch({ type: UPDATE_USER_LOADING, payload: false });
      authMsg('error', getCommonError(error));
    }
  };
}

export function changePassword(body) {
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_PASSWORD, payload: true });
      await axios.put(`/user/password`, body);
      NotificationManager.success(
        t('store.messages.password_updated'),
        t('common.success')
      );
      dispatch({ type: UPDATE_PASSWORD, payload: false });
    } catch (error) {
      dispatch({ type: UPDATE_PASSWORD, payload: false });
      NotificationManager.error(getCommonError(error));
    }
  };
}

export function setOrgGoalDocument(data) {
  return async (dispatch, getState) => {
    try {
      const user = cloneDeep(getState().authentication.user);
      Object.assign(user.organisation, data);
      dispatch({ type: GET_USER, payload: user });
    } catch (error) {
      // console.error('setOrgGoalDocument -> error', error);
      // NotificationManager.error(getCommonError(error))
    }
  };
}

export function helpTips(data) {
  return async (dispatch, getState) => {
    try {
      const user = cloneDeep(getState().authentication.user);
      const options = Object.assign(user.options, data);
      const response = await axios.put(`/user/settings`, { options });
      dispatch({ type: GET_USER, payload: response.data });
    } catch (error) {
      // NotificationManager.error(getCommonError(error));
    }
  };
}

export function forgotPassword(email) {
  return async (dispatch) => {
    dispatch({ type: FORGOT_USER_LOADING, payload: true });
    try {
      await axios.post(`/auth/forgot-password`, {
        username: email,
      });
      dispatch({ type: FORGOT_USER_LOADING, payload: false });
      NotificationManager.success(
        t('store.messages.password_reset_email_sent'),
        t('common.success')
      );
      return true;
    } catch (error) {
      NotificationManager.error(getCommonError(error));
      dispatch({ type: FORGOT_USER_LOADING, payload: false });
    }
  };
}

export function getForgotVerification(token, navigate) {
  return (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        await axios.get('/auth/verify', {
          params: {
            token,
          },
        });
        resolve();
      } catch (error) {
        navigate && navigate('/password-link-expired');
        reject(error);
      }
    });
  };
}

export function checkMailVerified(token) {
  return async (dispatch) => {
    dispatch({ type: USER_MAIL_VERIFICATION_LOADING, payload: true });
    try {
      const res = await axios.get('/auth/verify-mail', {
        params: {
          token,
        },
      });
      dispatch({ type: USER_MAIL_VERIFICATION, payload: res.data });
      dispatch({ type: USER_MAIL_VERIFICATION_LOADING, payload: false });
    } catch (error) {
      dispatch({ type: USER_MAIL_VERIFICATION_LOADING, payload: false });
      authMsg('error', getCommonError(error));
    }
  };
}

export function resetPassword(form, navigate) {
  return async (dispatch) => {
    dispatch({ type: RESET_USER_LOADING, payload: true });
    try {
      await axios.post(`/auth/reset-password`, form);
      dispatch({ type: RESET_USER_LOADING, payload: false });
      navigate && navigate('/password-changed');
      NotificationManager.success(
        t('store.messages.password_reset_success'),
        t('common.success')
      );
    } catch (error) {
      NotificationManager.error(getCommonError(error));
      dispatch({ type: RESET_USER_LOADING, payload: false });
    }
  };
}

export function registerRequest(token = null, navigate) {
  return async (dispatch) => {
    dispatch({ type: REGISTER_LOADING, payload: true });
    return new Promise(async (resolve, reject) => {
      try {
        let response = await axios.get('/register/request', {
          params: {
            token,
          },
        });
        dispatch({
          type: SET_REG_REQUEST_USER,
          payload: response.data,
        });
        dispatch({ type: REGISTER_LOADING, payload: false });
        resolve();
      } catch (error) {
        navigate && navigate('/link-expired');
        dispatch({ type: REGISTER_LOADING, payload: false });
        reject(getCommonError(error));
      }
    });
  };
}

export function userRegister(form, navigate) {
  return async (dispatch) => {
    dispatch({ type: REGISTER_USER_LOADING, payload: true });
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.post(
          '/register/submit-invite-registration',
          form
        );
        dispatch({ type: REGISTER_USER_LOADING, payload: false });
        navigate && navigate(`/account/verification/${response.data.token}`);
        resolve();
      } catch (error) {
        dispatch({ type: REGISTER_USER_LOADING, payload: false });
        //dispatch({ type: ERROR_HANDLING, payload: getCommonError(error) });
        navigate && navigate('/link-expired');
        reject(getCommonError(error));
      }
    });
  };
}
